/**
 * /accept-invite — landing page for an invite email link (WARD-108).
 *
 * Gated by `requireAuth`: an unauthenticated visitor is bounced to
 * `/login?return_to=/accept-invite?token=…` (see `lib/auth.ts`), so by the
 * time this component renders the user is signed in. We then POST the token
 * to the API; on success the new membership is created and we navigate to
 * the dashboard with the new org selected.
 *
 * Error states are surfaced as a small card with a clear next step rather
 * than a toast, because the user landed here from an email — they need to
 * know what went wrong before they can recover.
 */

import { Button } from "@/components/ui/button";
import { ApiClientProvider, OrgProvider, useApiClient, useOrg } from "@/lib/api";
import { requireAuth } from "@/lib/auth";
import { ToastProvider } from "@/lib/toast";
import { WardkeyAPIError } from "@wardkey/sdk";
import { useEffect, useState } from "react";
import { type LoaderFunctionArgs, useNavigate, useSearchParams } from "react-router";

export const acceptInviteLoader = requireAuth;

export function AcceptInviteRoute() {
  return (
    <ApiClientProvider>
      <ToastProvider>
        <OrgProvider>
          <AcceptInviteInner />
        </OrgProvider>
      </ToastProvider>
    </ApiClientProvider>
  );
}

type AcceptState =
  | { kind: "working" }
  | { kind: "success"; orgName: string }
  | { kind: "error"; title: string; body: string };

/** Exported for tests — bypasses the built-in provider tree so tests can
 * inject a custom fetch via their own ApiClientProvider wrap. */
export function AcceptInviteInner() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const client = useApiClient();
  const { refresh: refreshOrgs } = useOrg();
  const navigate = useNavigate();
  const [state, setState] = useState<AcceptState>({ kind: "working" });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on token change; client/refreshOrgs/navigate are referentially stable per provider instance
  useEffect(() => {
    if (!token) {
      setState({
        kind: "error",
        title: "Missing invite token",
        body: "The link you followed is incomplete. Open the original email and click the button again.",
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await client.invites.accept(token);
        if (cancelled) return;
        // Persist the new active org so the dashboard lands in the right
        // context without a flash of the previous org.
        try {
          window.localStorage.setItem("wk_active_org", result.org.org_id);
        } catch {
          // Private-mode storage failure is fine — OrgProvider will pick a
          // membership on first load.
        }
        refreshOrgs();
        setState({ kind: "success", orgName: result.org.name });
        // Brief pause so the user reads the success card before redirect.
        window.setTimeout(() => {
          if (!cancelled) navigate("/", { replace: true });
        }, 1200);
      } catch (err) {
        if (cancelled) return;
        setState({ kind: "error", ...errorPresentation(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="mx-auto flex min-h-screen max-w-lg items-center justify-center px-4">
      <div className="w-full rounded-xl border border-border bg-card p-8 shadow-[0_8px_30px_rgba(0,0,0,0.4)] backdrop-blur">
        {state.kind === "working" && <WorkingCard />}
        {state.kind === "success" && <SuccessCard orgName={state.orgName} />}
        {state.kind === "error" && <ErrorCard title={state.title} body={state.body} />}
      </div>
    </div>
  );
}

function WorkingCard() {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <span className="dot-pulse mr-2 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground align-middle" />
        accepting invite
      </div>
      <h1 className="mt-4 text-2xl font-medium tracking-tight text-foreground">
        Joining the organization…
      </h1>
      <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
        Verifying the token and adding you to the team. This usually takes a second.
      </p>
    </div>
  );
}

function SuccessCard({ orgName }: { orgName: string }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        welcome in
      </div>
      <h1 className="mt-4 text-2xl font-medium tracking-tight text-foreground">
        You're now a member of <span className="text-foreground">{orgName}</span>.
      </h1>
      <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
        Taking you to the dashboard…
      </p>
    </div>
  );
}

function ErrorCard({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
        invite not accepted
      </div>
      <h1 className="mt-4 text-2xl font-medium tracking-tight text-foreground">{title}</h1>
      <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">{body}</p>
      <Button asChild size="sm" className="mt-6">
        <a href="/">
          Go to dashboard
          <span aria-hidden>→</span>
        </a>
      </Button>
    </div>
  );
}

function errorPresentation(err: unknown): { title: string; body: string } {
  if (err instanceof WardkeyAPIError) {
    if (err.code === "invite_email_mismatch") {
      return {
        title: "Invite is for a different email",
        body: "This invite was sent to someone else. Sign in with the matching account, or ask the inviter to re-send to your email.",
      };
    }
    if (err.code === "invite_expired" || err.status === 410) {
      return {
        title: "This invite has expired",
        body: "Invitations are valid for 14 days. Ask an owner or admin to send you a fresh one.",
      };
    }
    if (err.code === "not_found" || err.status === 404) {
      return {
        title: "Invite no longer valid",
        body: "The link doesn't match any pending invite. It may have been revoked, already used, or the URL was mangled by your mail client.",
      };
    }
  }
  return {
    title: "Something went wrong",
    body:
      err instanceof Error && err.message
        ? err.message
        : "We couldn't accept the invite. Try again, or ask the inviter to re-send.",
  };
}

// Re-export the loader type so main.tsx can wire it up cleanly without
// importing `LoaderFunctionArgs` separately.
export type { LoaderFunctionArgs };
